import React, { useEffect, useMemo, useState } from "react";

import homepageEN from "../content/homepageContent.en.json";
import homepageHI from "../content/homepageContent.hi.json";

import HeroSection from "../components/homepage/HeroSection";
import TextSection from "../components/homepage/TextSection";
import BulletSection from "../components/homepage/BulletSection";
import ComparisonSection from "../components/homepage/ComparisonSection";
import NotHoroscopeBar from "../components/homepage/NotHoroscopeBar";
import PipelineSection from "../components/homepage/PipelineSection";
import TrustStrip from "../components/homepage/TrustStrip";
import PreviewSection from "../components/homepage/PreviewSection";
import ReportDepthSection from "../components/homepage/ReportDepthSection";
import InsightFlowSection from "../components/homepage/InsightFlowSection";
import TabbedSection from "../components/homepage/TabbedSection";
import CtaSection from "../components/homepage/CtaSection";
import QuietFooter from "../components/homepage/QuietFooter";

import "../styles/ui-foundation.css";
import "../styles/spacing-tokens.css";
import "../styles/homepage-premium.css";


export default function HomePage() {
  const [lang, setLang] = useState("en");

  // 1) Detect language on first load
  useEffect(() => {
    const saved = localStorage.getItem("sx_lang");
    if (saved === "hi" || saved === "en") {
      setLang(saved);
      return;
    }

    // Optional auto-detect
    const browserLang = (navigator.language || "").toLowerCase();
    if (browserLang.startsWith("hi")) {
      setLang("hi");
      localStorage.setItem("sx_lang", "hi");
    }
  }, []);

  // 2) Pick content
  const homepageContent = useMemo(() => {
    return lang === "hi" ? homepageHI : homepageEN;
  }, [lang]);

  const { sections, footer, launch_mode } = homepageContent;

  return (
    <main className="sx-home">
      <div className="sx-page">
        {sections.map((section) => {
          let content = null;

          switch (section.type) {
            case "hero":
              content = (
                <HeroSection
                  {...section.content}
                  launchMode={launch_mode}
                />
              );
              break;

            case "text_section":
              content = <TextSection {...section.content} />;
              break;

            case "bullet_section":
              content = <BulletSection {...section.content} />;
              break;

            case "comparison_section":
              content = <ComparisonSection {...section.content} />;
              break;

            case "not_horoscope_bar":
              content = <NotHoroscopeBar items={section.content.items} />;
              break;

            case "pipeline_section":
              content = <PipelineSection {...section.content} />;
              break;

            case "trust_strip":
              content = <TrustStrip items={section.content.items} />;
              break;

            case "preview_section":
              content = <PreviewSection {...section.content} />;
              break;

            case "report_depth_section":
              content = <ReportDepthSection {...section.content} />;
              break;

            case "insight_flow_section":
              content = <InsightFlowSection {...section.content} />;
              break;

            case "tabbed_section":
              content = <TabbedSection tabs={section.content.tabs} />;
              break;

            case "cta_section":
              content = (
                <CtaSection
                  heading={section.content.heading}
                  text={section.content.body?.join(" ")}
                  cta={section.content.primary_cta}
                  launchMode={launch_mode}
                />
              );
              break;

            default:
              return null;
          }

          if (!content) return null;

          const sectionClass =
            section.type === "hero"
              ? "sx-section sx-hero"
              : section.type === "cta_section"
              ? "sx-section sx-section--lg"
              : section.type === "not_horoscope_bar"
              ? "sx-nohoroscope-wrap"
              : "sx-section";

          return (
            <section key={section.id} className={sectionClass}>
              {content}
            </section>
          );
        })}

        {footer?.type === "quiet_footer" && (
          <div className="sx-footer">
            <QuietFooter content={[footer.content]} />
          </div>
        )}
      </div>
    </main>
  );
}
